// src/pages/RecomendacoesPage.js

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Button
} from '@mui/material';
import { 
  School as SchoolIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import api from '../services/api';
import PerfilForm from '../components/Recomendacoes/PerfilForm';
import RecomendacaoList from '../components/Recomendacoes/RecomendacaoList';
import RecomendacoesOverview from '../components/Recomendacoes/RecomendacoesOverview';
import useAuth from '../hooks/useAuth';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function RecomendacoesPage() {
  const [recomendacoes, setRecomendacoes] = useState([]);
  const [perfil, setPerfil] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  const carregarDados = useCallback(async () => {
    if (!user?.id || !user?.estudanteId) {
      setError('Usuário não identificado');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const [recomendacoesRes, perfilRes] = await Promise.all([
        api.get(`/recomendacoes/${user.id}`),
        api.get(`/perfil-academico/${user.estudanteId}`)
      ]);

      const recomendacoesData = recomendacoesRes.data?.recomendacoes || [];

      const formattedRecomendacoes = recomendacoesData.map(rec => {
        const justificativa = rec.justificativa || '';
        let nomeCurso = '';
        let nomeInstituicao = '';

        if (justificativa.includes('Curso: ')) {
          const cursoPart = justificativa.split('Curso: ')[1];
          if (cursoPart.includes(' na ')) {
            nomeCurso = cursoPart.split(' na ')[0];
            nomeInstituicao = cursoPart.split(' na ')[1];
          } else {
            nomeCurso = cursoPart;
          }
        }

        return {
          ...rec,
          curso: {
            nome: nomeCurso || 'Curso não disponível',
            instituicao: {
              id: rec.cursoId,
              nome: nomeInstituicao || 'Instituição não disponível',
            },
          },
        };
      });

      setRecomendacoes(formattedRecomendacoes);
      setPerfil(perfilRes.data);

    } catch (error) {
      console.error('Erro ao carregar recomendações:', error);
      setError('Não foi possível carregar as recomendações. Tente novamente mais tarde.');
      toast.error('Erro ao carregar recomendações');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, user?.estudanteId]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const handlePerfilSubmit = async (dadosPerfil) => {
    if (!user?.id || isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);

      await api.post(`/perfil-academico/${user.estudanteId}`, dadosPerfil);

      toast.success('Perfil atualizado com sucesso');
      await carregarDados();

    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      setError('Não foi possível atualizar o perfil. Tente novamente mais tarde.');
      toast.error('Erro ao atualizar perfil');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreInscricao = async (recomendacao) => {
    if (!user?.id || !recomendacao?.cursoId) return;
  
    try {
      const queryParams = new URLSearchParams({
        campanhaId: recomendacao.campanhaId || '1',
        cursoId: recomendacao.cursoId,
        instituicaoId: recomendacao.curso?.instituicao?.id,
        cursoNome: recomendacao.curso?.nome || '',
        instituicaoNome: recomendacao.curso?.instituicao?.nome || '',
        recomendacaoId: recomendacao.id || ''
      }).toString();
  
      navigate(`/pre-inscricoes/criar?${queryParams}`);
  
    } catch (error) {
      console.error('Erro ao processar redirecionamento:', error);
      setError('Não foi possível prosseguir com a pré-inscrição. Tente novamente mais tarde.');
      toast.error('Erro ao processar pré-inscrição');
    }
  };

  const handleRetry = () => {
    carregarDados();
  };

  if (isLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert 
          severity="error"
          sx={{ mb: 4 }}
        >
          {error}
        </Alert>
        <Button onClick={handleRetry} variant="outlined">
          Tentar Novamente
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          py: 6,
          mb: 4,
          textAlign: 'left',
          borderRadius: '16px',
          position: 'relative',
          background: 'linear-gradient(135deg, #1976d2 0%, #64b5f6 100%)',
          color: 'white',
          px: 4,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        {/* Botão "Voltar" */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')} // Altere '/dashboard' para a rota desejada
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              },
              textTransform: 'none'
            }}
          >
            Voltar
          </Button>
        </Box>

        <Box
          sx={{
            position: 'relative',
            zIndex: 2
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
              background: 'linear-gradient(45deg, #fff 30%, #e3f2fd 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Recomendações Vocacionais
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              fontWeight: 400,
              maxWidth: '600px',
              lineHeight: 1.5,
              fontSize: { xs: '1rem', md: '1.2rem' }
            }}
          >
            Encontre os melhores cursos baseados no seu perfil acadêmico
          </Typography>
        </Box>

        {/* Elementos decorativos */}
        <Box
          sx={{
            position: 'absolute',
            right: 40,
            top: '50%',
            transform: 'translateY(-50%)',
            display: { xs: 'none', md: 'block' }
          }}
        >
          <SchoolIcon
            sx={{
              fontSize: '5rem',
              opacity: 0.2,
              transform: 'rotate(-15deg)'
            }}
          />
        </Box>

        {/* Ondas decorativas */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '50px',
            overflow: 'hidden',
            zIndex: 1
          }}
        >
          <svg
            viewBox="0 0 500 150"
            preserveAspectRatio="none"
            style={{
              height: '100%',
              width: '100%'
            }}
          >
            <path
              d="M0.00,49.98 C150.00,150.00 349.20,-50.00 500.00,49.98 L500.00,150.00 L0.00,150.00 Z"
              style={{
                stroke: 'none',
                fill: '#fff',
                opacity: 0.1
              }}
            />
          </svg>
        </Box>
      </Box>

      {!perfil ? (
        <Box sx={{ mb: 8 }}>
          <PerfilForm 
            onSubmit={handlePerfilSubmit}
            isSubmitting={isSubmitting}
          />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 8 }}>
            <RecomendacoesOverview 
              recomendacoes={recomendacoes} 
              perfil={perfil}
              minimal={false}
              isLoading={isLoading}
              onPreInscricao={handlePreInscricao}
            />
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 4 }}>
              Cursos Recomendados
            </Typography>
            {recomendacoes.length > 0 ? (
              <RecomendacaoList 
                recomendacoes={recomendacoes} 
                onPreInscricao={handlePreInscricao}
                isSubmitting={isSubmitting}
              />
            ) : (
              <Alert severity="info">
                Nenhuma recomendação encontrada. Atualize seu perfil para receber recomendações personalizadas.
              </Alert>
            )}
          </Box>
        </>
      )}
    </Container>
  );
}
